import {
  useUpsertPersonAccessLevelMutation,
  PeopleDocument,
} from "@/features/people/graphql/generated";
import { useToast } from "@/shared/contexts/toast.context";

// Map an access-level change target to the exact People query variables for that
// resource, so we refetch only the table that is on screen. Refetching every
// "People" query by name also reran the account-members query (People({})), which
// returns each person with only their ACCOUNT role. Because all People queries are
// normalized into the same Person.roles field, that partial result overwrote the
// richer resource-scoped roles in Apollo's cache, making every member's role and
// access level disappear until a reload. Scoping the refetch avoids the clobber.
const buildPeopleFilter = (objectType: string, objectId: number) => {
  switch (objectType) {
    case "team":
      return { team_id: objectId };
    case "project":
      return { project_id: objectId };
    case "organization":
      return { org_id: objectId };
    case "account":
    default:
      return {};
  }
};

export const usePersonAccessLevelChange = () => {
  const { showToast } = useToast();

  const [updatePersonAccessLevel, { loading }] =
    useUpsertPersonAccessLevelMutation();

  const handleUpdatePersonAccessLevel = async ({
    personId,
    access_level,
    objectType,
    objectId,
  }: {
    personId: number;
    access_level: string;
    objectType: string;
    objectId: number;
  }) => {
    try {
      const { data } = await updatePersonAccessLevel({
        variables: {
          personId,
          type: objectType,
          access_level,
          objectId,
        },
        refetchQueries: [
          {
            query: PeopleDocument,
            variables: { filter: buildPeopleFilter(objectType, objectId) },
          },
        ],
        onError: (error) => {
          showToast({
            message: "Error!",
            description: error.message,
            type: "error",
          });
        },
      });

      if (data?.upsertPersonAccessLevel?.success) {
        showToast({
          message: "Success!",
          description:
            data?.upsertPersonAccessLevel?.message ||
            " Access level updated successfully",
          type: "success",
        });
        return true;
      } else {
        showToast({
          message: "Oops!",
          description:
            data?.upsertPersonAccessLevel?.message ||
            " Access level update failed",
          type: "error",
        });
        return false;
      }
    } catch (error) {
      return false;
    }
  };

  return { handleUpdatePersonAccessLevel, loading: false };
};
